import { Controller } from "@hotwired/stimulus"
import { hideElement, showElement } from "../src/dom"
import { EventBus } from "../src/event_bus"
import VideoUploader from "../src/video_uploader"

const isValidFile = (file) => {
  if (!file.type) { return false }

  const baseType = file.type.split("/")[0]
  return baseType == "video"
}

const progressItemTemplate = (filename) => {
  return `
    <div class="d-flex flex-direction-row align-items-center">
      <div
        class="video-upload-progress__icon is--in-progress"
        data-video-upload-progress-target="iconInProgress"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M8.71,7.71,11,5.41V15a1,1,0,0,0,2,0V5.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42l-4-4a1,1,0,0,0-.33-.21,1,1,0,0,0-.76,0,1,1,0,0,0-.33.21l-4,4A1,1,0,1,0,8.71,7.71ZM21,12a1,1,0,0,0-1,1v6a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V13a1,1,0,0,0-2,0v6a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V13A1,1,0,0,0,21,12Z"/></svg>
      </div>
      <div
        class="video-upload-progress__icon is--completed d-none"
        data-video-upload-progress-target="iconCompleted"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,.71.29,1,1,0,0,0,.7-.29l5-5a1,1,0,0,0,0-1.42A1,1,0,0,0,14.72,8.79ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"/></svg>
      </div>
      <div
        class="video-upload-progress__icon is--failed d-none"
        data-video-upload-progress-target="iconFailed"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"/></svg>
      </div>

      <div class="video-upload-progress__name">${filename}</div>

      <div
        class="video-upload-progress__percentage"
        data-video-upload-progress-target="percentageDisplay"
      ></div>
    </div>

    <div
      class="video-upload-progress__progress-wrapper progress"
      data-video-upload-progress-target="progressWrapper"
    >
      <div
        class="video-upload-progress__progress-bar progress-bar progress-bar-striped bg-success"
        data-video-upload-progress-target="progressBar"
        style="width: 0%;"
      ></div>
    </div>

    <div
      class="video-upload-progress__message-uploaded fw-bold text-success ms-3 d-none"
      data-video-upload-progress-target="messageCompleted"
    >Das Video wurde hochgeladen und wird nun im Hintergrund encodiert.</div>
  `
}

export default class extends Controller {
  static targets = ["alert", "fileInput", "progressList", "progressItem", "videoCounter"]
  static values = {
    url: String,
    videoLimit: Number,
    numberOfVideos: Number
  }

  connect() {
    this.uploads = {}
  }

  async uploadFiles() {
    const validFiles = Array.from(this.fileInputTarget.files).filter(file => isValidFile(file))

    if (validFiles.length > this.numberOfAvailableVideos) {
      this.alertTarget.innerHTML = `In Ihrem Paket sind nur noch <strong>${this.numberOfAvailableVideos}</strong> Videos verfügbar.`
      showElement(this.alertTarget)
    } else {
      hideElement(this.alertTarget)
    }

    if (validFiles.length) { showElement(this.progressListTarget) }

    await Promise.all(validFiles.map(async (file) => {
      this.numberOfVideosValue += 1
      this.videoCounterTarget.innerHTML = this.formattedNumberOfVideos
      await this.startUpload(file)
    }))

    this.fileInputTarget.value = null
  }

  async startUpload(file) {
    const uploader = new VideoUploader(file, this.urlValue)
    this.createProgressItem(uploader)

    const videoCreated = await uploader.fetchUploadData()

    if (!videoCreated) {
      EventBus.emit("video-upload:error", { id: uploader.id })
    } else {
      uploader.startUpload()
    }
  }

  createProgressItem(uploader) {
    const element = document.createElement("div")
    element.classList.add("video-upload-progress")
    element.dataset.controller = "video-upload-progress"
    element.dataset.videoUploadProgressIdValue = uploader.id
    element.dataset.videoUploadProgressFilenameValue = uploader.filename
    element.dataset.videoUploadTarget = "progressItem"

    element.innerHTML = progressItemTemplate(uploader.filename)

    this.progressListTarget.prepend(element)
    this.uploads[uploader.id] = {
      uploader: uploader,
      element: element
    }
  }

  get numberOfAvailableVideos() { return this.videoLimitValue - this.numberOfVideosValue }
  get formattedNumberOfVideos() {
    return (this.numberOfVideosValue).toLocaleString(
      undefined,
      { maximumFractionDigits: 0 }
    )
  }
}
